/**
 * @Author: Zero
 * @Date: 2026-07-24 11:08:29
 * @Explain: 消息推送
 */
import type { TExperimentStatus } from '@/pages/pushExperiment/utils/enum/list';

/** 基础 API 响应（所有推送接口共用） */
export interface PushApiResponse<T = unknown> {
  ret: number;
  code: number;
  msg?: string;
  data?: T;
}

/** 配置类型枚举：1=默认配置 0=实验配置 */
export enum ConfigType {
  /** 默认配置 */
  DEFAULT = 1,
  /** 实验配置 */
  EXPERIMENT = 0,
  /** 全部待审批总条数 */
  ALL = -1,
}

/** 查看默认推送配置 */
export interface GetDefaultConfigParams {
  gameId: number;
}

/** 默认推送变更记录 */
export interface GetOperationLogParams {
  gameId: number;
  configType: ConfigType;
}

/** 下载配置详情
 *  configType: 1=默认配置 0=实验配置
 */
export interface DownloadConfigParams {
  game_id: number;
  config_id: number;
  config_type: ConfigType;
}

/** 预览配置项 */
export interface ViewConfigItemParams {
  gameId: number;
  configId: number;
  configType: ConfigType;
  groupName: string;
}

/** 查看待审核配置详情 */
export interface ViewConfigParams {
  gameId: number;
  configId: number;
  configType: ConfigType;
}

/** 推送状态切换操作类型 */
export enum PushStatusOperation {
  /** 重启/继续推送 */
  RESUME = 0,
  /** 暂停推送 */
  PAUSE = 1,
  /** 撤回申请 */
  REVOKE = 2,
}

/** 暂停/重启默认推送 */
export interface UpdateConfigStatusParams {
  gameId: number;
  configId: number;
  /** 0=重启/继续 1=暂停（参考 PushStatusOperation） */
  status: PushStatusOperation;
  configType: ConfigType;
}

/** 更新默认推送配置（发布为新的默认配置）
 *  对应接口：POST /dapi/push_notification/update_default_config
 *  - configId: 实验配置 Id（协议要求 string）
 *  - groupName: 配置推送分组 Id
 *  - configName: 配置名字（组合后的名字）
 */
export interface UpdateDefaultConfigParams {
  gameId: number;
  configId: number;
  groupName: string;
  configName: string;
}

/** 搜索可选配置列表 */
export interface SearchConfigListParams {
  gameId: number;
  searchField?: string;
  pageSize: number;
  pageNum: number;
}

/** 搜索实验列表 */
export interface SearchExpListParams {
  gameId: number;
  isArchived: 0 | 1;
  pageSize: number;
  pageIndex: number;
}

/** 审批中数量 */
export interface ConfigCountParams {
  gameId: number;
  status: number;
  configType: ConfigType;
}

export interface ConfigCountData {
  count: number;
}

/** 待审核列表 */
export interface PendingConfigListParams {
  gameId: number;
}

export interface PendingDefaultConfigDetail {
  prevExpConfigId?: number;
  groupName?: string;
}

export interface PendingConfigItem {
  configId: number;
  configType: ConfigType;
  configName: string;
  defaultConfigDetail?: PendingDefaultConfigDetail;
  appliedTime: string;
  createdBy: string;
  isCreator: 0 | 1;
  // 兼容文档示例里将字段直接平铺的情况
  prevExpConfigId?: number;
  groupName?: string;
}

/** 配置审批 */
export interface ApproveConfigParams {
  gameId: number;
  configId: number;
  configType: ConfigType;
  /** 0=审批驳回 1=审批通过 */
  isApprove: 0 | 1;
}

export interface ApproveConfigResult {
  errorType?: number;
  accountName?: string;
  approvedTime?: string;
}

/** 结束实验
 *  对应接口：POST /dapi/push_notification/finish_exp
 */
export interface FinishExpParams {
  gameId: number;
  configId: number;
}

/** 查询可推送玩家数量
 *  对应接口：POST /dapi/push_notification/view_player_count
 */
export interface ViewPlayerCountParams {
  gameId: number;
}

/** 可推送玩家数量响应 */
export interface ViewPlayerCountData {
  count: number;
}

/** 下载实验配置
 * @deprecated 保留类型仅为兼容旧调用，新代码统一使用 downloadConfig(configType=ConfigType.EXPERIMENT)
 */
export interface DownloadExpConfigParams {
  gameId: number;
  configId: string;
}

/** 校验实验配置
 *  服务端检查：名称重复 / 实验正在运行 等
 *  - configStartDate: 最早推送时间的日期部分（YYYY-MM-DD）
 *  - configEndDate: 最晚推送时间的日期部分（YYYY-MM-DD）
 *  - configJson: 用户上传的原始 JSON 字符串（透传给后端做更精细的校验）
 */
export interface CheckJsonParams {
  gameId: number;
  configName: string;
  configStartDate: string;
  configEndDate: string;
  configJson: string;
  defaultLang: string;
}

/** 校验实验配置响应中的错误类型枚举 */
export enum CheckJsonErrorType {
  /** 1001 配置名称重复 */
  NAME_DUPLICATE = 1001,
  /** 1002 实验配置正在运行 */
  CONFIG_RUNNING = 1002,
}

/** 校验实验配置响应体：error_list 为空表示通过
 *  注意：本接口关闭了 switchReqToUnderline（详见 checkJson 方法注释），响应保持 snake_case。
 */
export interface CheckJsonErrorItem {
  error_type: number;
  error_msg?: string;
  detail?: string;
}

export interface CheckJsonResult {
  error_list: CheckJsonErrorItem[];
}

/** 新建实验推送 / 校验实验配置 共用的推送项结构 */
export interface ConfigDetailItem {
  day: number;
  title: string;
  body: string;
  push_id: string;
  sending_time: string;
  push_id_gen_type?: number; // 0=AI 自动生成 1=导入 CSV
  ttl?: string;
  other_lang?: Record<
    string,
    { push_id: string; title?: string; body?: string }
  >;
}

/** 检查json文件 */
export interface CheckJsonV2Params {
  gameId: number;
  configName: string;
  configStartDate: string;
  configEndDate: string;
  configJson: string;
  isPushIdGen: 0 | 1; // 0=手动输入 1=自动生成
  actionType: 0 | 1; // 0=创建 1=编辑
  configId?: number;
}

/** check_json响应中的配置项 */
export interface CheckJsonV2ConfigItem {
  day: number;
  title: string;
  body: string;
  push_id: string;
  sending_time: string;
  push_id_gen_type?: number;
  other_lang?: Record<
    string,
    { push_id?: string; title?: string; body?: string }
  >;
}

/** check_json（v2）响应 data（error_list 为空表示校验通过）*/
export interface CheckJsonV2Result {
  /** 错误列表（error_type 非 0 表示校验失败，透传提示） */
  error_list: { error_type: number; detail?: string }[];
  /** 默认语言 */
  default_lang?: string;
  /** 校验通过后回填的分组内容（key=分组 id，value=该组配置项数组） */
  config_details?: Record<string, CheckJsonV2ConfigItem[]>;
}

/** 新建实验推送*/
export interface CreateExpConfigParams {
  gameId: number;
  configName: string;
  configStartDate: string;
  configEndDate: string;
  /** key=分组名，value=该分组下的推送详情数组 */
  configDetails: Record<string, ConfigDetailItem[]>;
  /** 配置原 JSON（用户上传的原文，字符串化） */
  configJson: string;
  /** 默认语言代码 */
  defaultLang: string;
}

/** 实验配置语言详情项（与 get_user_lang.lang_list 元素原样透传：lang_code/cn_name/en_name/user_percentage） */
export interface ExpLangDetailItem {
  lang_code: string;
  cn_name?: string;
  en_name?: string;
  user_percentage?: number | string;
  [key: string]: unknown;
}

/** create_exp_config 请求体顶层附加结构 */
export interface ExpConfigDetail {
  create_type: number;
  lang_details?: ExpLangDetailItem[];
  group_details?: {
    exp_id?: string;
    group_list?: AbcGroupItem[];
    sync_time?: string;
    [key: string]: unknown;
  };
}

/** 新建/编辑实验配置*/
export interface CreateExpConfigV2Params {
  gameId: number;
  configName: string;
  configStartDate: string;
  configEndDate: string;
  /** key=分组 id，value=该分组下的推送项数组（与 default_config 的 config_details 结构一致） */
  configDetails: Record<string, ConfigDetailItem[]>;
  /** 完整配置原文 JSON（exp_name / push_content / 日期 / default_lang），字符串化透传存储 */
  configJson: string;
  /** 创建方式：0=JSON 文件上传 1=手动创建（创建向导）；保存草稿与提交审批均需透传 */
  createType: number;
  /** 0=提交审批 1=保存草稿 */
  isDraft: 0 | 1;
  /** 语言与分组快照（create_exp_config 顶层附加字段 config_detail） */
  configDetail?: ExpConfigDetail;
  /** 编辑场景必传（action_type=1）；创建场景省略 */
  configId?: number;
  /** 0=创建 1=编辑 */
  actionType: 0 | 1;
}

/** create_exp_config 业务校验错误项（响应 data.error_list 元素） */
export interface CreateExpConfigErrorItem {
  /** 错误种类：如 1001=配置名称重复 1002=配置正在运行 */
  error_type?: number;
  detail?: string;
}

/** create_exp_config 响应 data */
export interface CreateExpConfigResult {
  /** 业务校验错误列表；为空 / 为 null 表示校验通过 */
  error_list: CreateExpConfigErrorItem[] | null;
}

/** 配置项 */
export interface ConfigItem {
  day: number;
  sendingTime: string;
  pushId: string;
  title: string;
  body: string;
  /** firebase云端保留时间 */
  messageTtl?: string;
  otherLang?: Record<string, { pushId: string; title?: string; body?: string }>;
}

/** 默认推送配置详情 */
export interface OngoingConfigData {
  configId: number;
  configName: string;
  status: TExperimentStatus; // 0=未推送 1=推送中 2=已推送
  modifiedBy: string;
  approvedBy: string;
  configItems: ConfigItem[];
  prevExpConfigId: number;
  groupName: string;
  /** 默认语言代码 */
  defaultLang?: string;
  confStatus: number; // 1生效中 0为已停止
}

/** 审批中的默认推送配置 */
export interface ApprovingConfigData {
  configId: number;
  configName: string;
  appliedBy: string;
  appliedTime: string;
  configItems: ConfigItem[];
  prevExpConfigId: number;
  groupName: string;
  isCreator?: 0 | 1;
  /** 默认语言代码 */
  defaultLang?: string;
}

/** 默认推送配置详情 */
export interface DefaultConfigData {
  ongoingConfig?: OngoingConfigData;
  isApprovingConfigExist: 0 | 1;
  approvingConfig?: ApprovingConfigData;
  isConfigInitExist?: boolean;
}

/** 操作日志 */
export interface OperationLog {
  operationTime: string;
  operationType: 0 | 1 | 2 | 3 | 4;
  configId?: number;
  configName?: string;
  operationBy: string;
  /** 审核结果类型：1010=审批通过 1011=审批驳回（仅 operationType=3 时有值） */
  errorType?: number;
}

/** 配置列表项（搜索可选配置列表 响应）
 * configType: 1=默认配置 0=实验配置（供 viewConfig 区分来源）
 */
export interface ConfigListItem {
  configId: number;
  configName: string;
  configType: ConfigType;
  groupNames: string[];
}

/** 分页信息 */
export interface PageInfo {
  pageNum: number;
  pageSize: number;
  total: number;
  totalPage: number;
}

/** 实验列表项
 * expStatus 取值含义（详见 enum/list.ts EXPERIMENT_STATUS_VALUE）：
 * 0=待推送 1=推送中 2=完成 3=已停止 6=审批驳回 7=超时未审批 8=跑表超时中止 9=草稿
 */
export interface ExpListItem {
  configId: number;
  configName: string;
  pushCount: number;
  totalUserCount: number;
  pushedUserCount: number;
  expStatus: TExperimentStatus;
  /** 创建方式：0=JSON 文件上传（列表支持下载） 1=手动创建（列表支持配置详情/编辑）
   *  兼容两种返回位置：exp_list 旧字段行顶层 create_type；实际在 config_detail.create_type 回流 */
  createType?: number;
  /** 创建快照（exp_list / view_config 回流）：create_type=0 JSON 文件上传 / 1 页面手动创建 */
  configDetail?: {
    /** 0=JSON 文件上传 1=页面手动创建 */
    createType?: number;
  };
  expPushStartTime: string;
  appliedBy: string;
  approvedBy: string;
  expPushBy?: string;
  pushContent?: Record<string, RawConfigItem[]>;
  /** 默认语言代码（与 push_content 同级） */
  defaultLang?: string;
}

/** 名称重复检查结果
 * @deprecated 已升级为 checkJson，返回 errorList 结构。保留仅为兼容旧引用，新代码请使用 CheckJsonResult。
 */
export interface CheckNameResult {
  isNameDuplicate: 0 | 1;
}

/** 检查 push 实验名称是否重复*/
export interface CheckExpNameParams {
  /** 配置 Id：编辑时传自身 config_id 以排除自身重名；新建无则传 0 */
  configId: number;
  configName: string;
}

/** 检查 push 实验名称是否重复 */
export interface CheckExpNameResult {
  /** 后端返回字段 is_exist */
  isExist: boolean;
}

/** 用户语言项*/
export interface UserLangItem {
  lang_code: string;
  cn_name?: string;
  lang_name?: string;
  en_name?: string;
  user_percentage: number;
}

/** 获取用户语言 响应体*/
export interface GetUserLangResult {
  lang_list: UserLangItem[];
}

/** gen_ai_content 任务计划（submit 响应 data.plan） */
export interface AiGenPlan {
  slot_count?: number;
  item_count?: number;
  cycle_days?: number;
  languages?: string[];
  /** 任务预计耗时（ms）：轮询间隔建议 = estimated_duration_ms / 20，下限 2s、上限 15s */
  estimated_duration_ms?: number;
}

/** gen_ai_content 生成的一条推送内容（SUCCEEDED 后 data.push_content 元素） */
export interface AiGenContentItem {
  day: number;
  sending_time: string;
  title: string;
  body: string;
  /** 推送 ID（Databricks 侧生成） */
  push_id_ds: string;
  /** 附加语言本地化：key 为 lang_code（各语言 push_id_ds 由 AI 侧生成，提交时原样透传，缺失才兜底拼接） */
  other_lang?: Record<
    string,
    { title: string; body: string; push_id_ds?: string }
  >;
}

/** gen_ai_content 运行进度（status=RUNNING 时返回；PENDING/终态为 null，见文档 §4.2） */
export interface AiGenProgress {
  /** 当前流水线步骤：固定 1~5，单调不减（素材筛选/策略构建/主语言生成/多语言生成/校验） */
  step: number;
  /** 步骤 key（retrieve_knowledge 等），仅诊断用 */
  step_key?: string;
  /** 已完成条目数：跨第 3、4 步的全局计数（1、2、5 步期间不变） */
  completed_items?: number;
  /** 总条目数：恒等于 plan.item_count */
  total_items?: number;
}

/** gen_ai_content submit / status 响应 data（网关信封形态）：任务字段收在 data.content.predictions 内 */
export interface AiGenResult {
  content?: {
    /** 任务本体（job_id / status / plan / push_content / progress 等） */
    predictions?: AiGenPrediction;
  };
}

/** gen_ai_content 任务本体（content.predictions 内字段；submit 与 status 均返回，不同阶段部分字段缺失） */
export interface AiGenPrediction {
  job_id?: string;
  request_id?: string;
  /** PENDING / RUNNING / SUCCEEDED / FAILED / CANCELLED */
  status?: string;
  plan?: AiGenPlan;
  push_content?: AiGenContentItem[];
  /** FAILED / CANCELLED 时的失败信息 */
  failure?: { code?: string; message?: string };
  /** Databricks 侧业务错误（HTTP 200 + error 字段形式返回时） */
  error?: { code?: string; message?: string; type?: string };
  /** RUNNING 时的真实阶段进度；PENDING / 终态为 null */
  progress?: AiGenProgress | null;
}

/** 配置预览行数据（viewConfig 响应中的 configItems 项） */
export interface ConfigPreviewRow {
  day: number;
  sendingTime: string;
  pushId: string;
  title: string;
  body: string;
  /** firebase云端保留时间 */
  messageTtl?: string;
  otherLang?: Record<string, { pushId: string; title?: string; body?: string }>;
}

/** 原样返回的配置项（snake_case，避免 group key 被自动转换） */
export interface RawConfigItem {
  day: number;
  sending_time: string;
  push_id: string;
  title: string;
  body: string;
  firebase_cloud_time?: number;
  group_name?: string;
  /** 推送项来源：0=AI 自动生成 1=导入 CSV */
  push_id_gen_type?: number;
  /** firebase云端保留时间 */
  message_ttl?: string;
  other_lang?: Record<
    string,
    { push_id: string; title?: string; body?: string }
  >;
}

export interface RawExpConfigDetail {
  push_start_date: string;
  push_end_date: string;
  total_user_count: number;
  /** 实验循环天数（create 时随 config_json 携带） */
  exp_cycle_days?: number;
  push_content: Record<string, RawConfigItem[]>;
  /** 创建快照（回流）：config_detail.create_type=0 JSON 文件上传 / 1 页面手动创建；含 lang_details/group_details */
  config_detail?: ExpConfigDetail;
  /** 默认语言代码 */
  default_lang?: string;
}

export interface RawDefaultConfigDetail {
  prev_exp_config_id: number;
  group_name: string;
  config_items: RawConfigItem[];
  /** 默认语言代码 */
  default_lang?: string;
}

export interface RawViewConfigData {
  config_type: ConfigType;
  config_name: string;
  applied_by: string;
  applied_time: string;
  exp_config_detail?: RawExpConfigDetail;
  def_config_detail?: RawDefaultConfigDetail;
}

/** 实验分组项*/
export interface AbcGroupItem {
  group_id: string;
  group_name: string;
  group_type: string;
  group_percentage: number;
}

/** 获取 abc 内容 响应体*/
export interface AbcContentResult {
  exp_id: string;
  group_list: AbcGroupItem[];
  sync_time: string;
}
